/**
 * Equipment System Type Definitions
 * Phase 1: Data Structures & Catalog Foundation
 *
 * This file defines the complete type system for Loot and Consumable items
 * following the official Daggerheart SRD specifications.
 */

// ============================================================================
// METADATA & SOURCE TRACKING
// ============================================================================

/**
 * Source metadata for items (official SRD vs homebrew)
 */
export type ItemSource = 'official-srd-v1' | 'homebrew';

/**
 * Metadata tracking for all items
 */
export interface ItemMetadata {
  /** True for official SRD items */
  official: boolean;
  /** Source of the item */
  source: ItemSource;
  /** Creation timestamp (ISO date string) - null for official items */
  createdAt: string | null;
  /** Author's Firebase user ID - null for official items */
  authorId: string | null;
  /** Version number for tracking updates and errata */
  version: string;
}

// ============================================================================
// SCALING EXPRESSIONS
// ============================================================================

/**
 * Scaling variables that can be used in formulas
 */
export type ScalingVariable =
  | 'character_tier'
  | 'character_level'
  | 'proficiency'
  | 'stat';

/**
 * Scaling expression for tier-dependent or stat-dependent effects
 */
export interface ScalingExpression {
  type: ScalingVariable;
  /** Multiplier or fixed amount (optional) */
  amount?: number;
  /** Dependencies for formula evaluation */
  dependencies: string[];
  /** Human-readable description */
  description?: string;
}

// ============================================================================
// ITEM EFFECTS SYSTEM
// ============================================================================

/**
 * Types of effects items can have
 */
export enum ItemEffectType {
  // Healing & Resource Management
  HealHP = 'heal-hp',
  HealStress = 'heal-stress',
  RestoreArmor = 'restore-armor',
  GrantHope = 'grant-hope',

  // Damage & Combat
  DamageBonus = 'damage-bonus',
  Damage = 'damage',
  AreaDamage = 'area-damage',
  WeaponFeatureGrant = 'weapon-feature-grant',

  // Stat Modifiers (Temporary - not auto-applied)
  StatBoostTemporary = 'stat-boost-temporary',
  StatPenaltyTemporary = 'stat-penalty-temporary',
  StatBonusPermanent = 'stat-bonus-permanent',

  // Utility & Special Abilities
  AbilityGrant = 'ability-grant',
  FormChange = 'form-change',
  Environmental = 'environmental',
  KnowledgeTransfer = 'knowledge-transfer',
  CraftingRecipe = 'crafting-recipe',

  // High-Risk Effects
  GuaranteedCrit = 'guaranteed-crit',
  RandomOutcome = 'random-outcome',
  InstantKillRisk = 'instant-kill-risk',

  // General
  Feature = 'feature',
  Modifier = 'modifier',
  ResourceGeneration = 'resource-generation',
  Attachment = 'attachment',
  Activation = 'activation',
  Condition = 'condition',
  Utility = 'utility'
}

/**
 * Damage types for combat effects
 */
export type DamageType =
  | 'physical'
  | 'fire'
  | 'ice'
  | 'lightning'
  | 'poison'
  | 'magic'
  | 'psychic'
  | 'necrotic'
  | 'radiant';

/**
 * Range types for effects
 */
export type EffectRange =
  | 'self'
  | 'touch'
  | 'close'
  | 'far'
  | 'very-far';

/**
 * Area of effect shapes
 */
export type AreaType =
  | 'burst'
  | 'cone'
  | 'line'
  | 'radius';

/**
 * Duration types for temporary effects
 */
export type EffectDuration =
  | 'instant'
  | 'next-roll'
  | 'until-rest'
  | 'until-long-rest'
  | '1-hour'
  | '1-day'
  | 'permanent';

/**
 * Trait names for stat bonuses
 */
export type TraitName =
  | 'agility'
  | 'strength'
  | 'finesse'
  | 'instinct'
  | 'presence'
  | 'knowledge';

/**
 * Base interface for item effects
 * Effects describe what an item does but are NOT automatically applied
 */
export interface ItemEffect {
  effectType: ItemEffectType;
  description: string;

  // Healing effects
  healAmount?: number;
  healFormula?: string; // e.g., "1d4", "2d6+2"
  healType?: 'hp' | 'stress';

  // Damage effects
  damageFormula?: string; // e.g., "1d6", "3d8"
  damageType?: DamageType;
  damageDice?: string; // e.g., "d6", "d8", "d12"

  // Stat bonus effects
  stat?: TraitName;
  bonusFormula?: string; // e.g., "{tier}", "{proficiency}", "2", "{tier}+1"
  amount?: number;
  duration?: EffectDuration;

  // Combat effects
  range?: EffectRange;
  areaType?: AreaType;
  savingThrow?: string; // e.g., "reaction", "agility"

  // Activation
  activationType?: 'manual' | 'automatic' | 'passive';
  usesPerRest?: string; // e.g., "3x/short-rest", "1x/long-rest"

  // Weapon/armor features
  featureName?: string;
  featureDescription?: string;
  requiresAttachment?: boolean;
  attachmentTarget?: 'weapon' | 'armor';

  // Crafting
  recipeFor?: string; // Item ID that can be crafted
  requiredMaterials?: string;
  downtimeRequired?: boolean;

  // High-risk effects
  selfRiskDice?: string; // e.g., "d6"
  selfRiskThreshold?: number;

  // Timing
  timing?: string; // e.g., "downtime", "combat", "rest"

  // Flexible additional properties for specific effect types
  [key: string]: any;
}

// ============================================================================
// ITEM CONSTRAINTS SYSTEM
// ============================================================================

/**
 * Types of constraints that can be applied to items
 */
export type ItemConstraintType =
  | 'unique-category'
  | 'max-quantity'
  | 'requires-attachment'
  | 'tier-locked';

/**
 * Constraint interface for limiting item usage
 */
export interface ItemConstraint {
  type: ItemConstraintType;
  /** Category for unique-category constraints */
  category?: string;
  /** Limit value for max-quantity constraints */
  limit?: number;
  /** Max quantity constraint value */
  max?: number;
  /** Attachment target for requires-attachment constraints */
  attachTo?: 'weapon' | 'armor';
  /** Whether attachment requires no existing feature */
  requiresNoFeature?: boolean;
  /** User-facing constraint message */
  message?: string;
  /** Flexible value field for constraint-specific data */
  value?: any;
}

// ============================================================================
// ITEM MODIFIERS (Character Stat Effects)
// ============================================================================

/**
 * Modifier interface for items that affect character stats
 * These are automatically applied when item is in inventory (for loot)
 * or NOT applied (for consumables - player manages manually)
 */
export interface ItemModifier {
  id: string;
  source: ModifierSource;
  target: string; // e.g., 'traits.agility', 'evasion', 'armor'
  operation: ModifierOperation;
  value: number | ModifierFormulaObject;
  conditions?: ModifierCondition[];
  stackable: boolean;
  priority: number;
}

/**
 * Source of a modifier
 */
export interface ModifierSource {
  type: 'equipment' | 'spell' | 'class' | 'heritage' | 'stance' | 'temporary';
  id: string;
  name: string;
}

/**
 * Modifier operation types
 */
export type ModifierOperation = 'add' | 'multiply' | 'set' | 'replace';

/**
 * Formula object for dynamic modifier values
 */
export interface ModifierFormulaObject {
  type: 'trait_direct' | 'trait_half' | 'trait_multiply' | 'character_level' | 'character_tier' | 'expression';
  trait?: TraitName;
  value?: number;
  multiplier?: number;
  formula?: string;
  dependencies?: string[];
}

/**
 * Condition for conditional modifiers
 */
export interface ModifierCondition {
  type: 'equipment_not_equipped' | 'stat_greater_than' | 'stat_less_than' | 'tier_equals' | 'equipment' | 'domain_card_count';
  category?: string;
  equipped?: boolean;
  domain?: string;
  count?: number;
  stat?: string;
  value?: number;
}

// ============================================================================
// LOOT ITEMS (Reusable Items)
// ============================================================================

/**
 * Loot item subtypes
 */
export enum LootSubtype {
  Relic = 'relic',
  Attachment = 'attachment',
  CombatTool = 'combat-tool',
  Storage = 'storage',
  Recipe = 'recipe',
  Companion = 'companion',
  MagicalTool = 'magical-tool',
  Mobility = 'mobility',
  Tool = 'tool'
}

/**
 * Base interface for all items
 */
export interface ItemBase {
  id: string;
  name: string;
  type: 'loot' | 'consumable';
  category: string; // same as type for clarity
  subtype: LootSubtype | ConsumableSubtype;
  description: string;
  feature?: string;
  rarity?: string;
  tier?: 1 | 2 | 3 | 4;
  effects: ItemEffect[];
  modifiers: ItemModifier[];
  constraints: ItemConstraint[];
  metadata: ItemMetadata;
  tags: string[];
}

/**
 * Loot item (reusable equipment)
 */
export interface LootItem extends ItemBase {
  type: 'loot';
  subtype: LootSubtype;
  attachesTo?: 'weapon' | 'armor';
}

// ============================================================================
// CONSUMABLE ITEMS (Single-Use Items)
// ============================================================================

/**
 * Consumable item subtypes
 */
export enum ConsumableSubtype {
  Potion = 'potion',
  Poison = 'poison',
  Food = 'food',
  Scroll = 'scroll',
  Wand = 'wand',
  Reagent = 'reagent',
  Recipe = 'recipe'
}

/**
 * Consumable item (single-use)
 */
export interface ConsumableItem extends ItemBase {
  type: 'consumable';
  subtype: ConsumableSubtype;
  singleUse: true;
  maxHold: number; // typically 5
  usageText?: string;
}

// ============================================================================
// CHARACTER INVENTORY REFERENCES
// ============================================================================

/**
 * Reference to a loot item in character inventory
 * NOTE: Loot items are equipped via equipmentReferences array (same as weapons/armor)
 * This reference is only for organizing inventory display
 */
export interface LootReference {
  itemId: string;
  quantity?: number; // optional, for tracking duplicates
}

/**
 * Reference to a consumable item in character inventory
 */
export interface ConsumableReference {
  itemId: string;
  quantity: number; // how many of this consumable
}

// ============================================================================
// TYPE UNIONS & HELPERS
// ============================================================================

/**
 * Union type for any item
 */
export type AnyItem = LootItem | ConsumableItem;

/**
 * Union type for any item subtype
 */
export type ItemSubtype = LootSubtype | ConsumableSubtype;

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard for LootItem
 */
export function isLootItem(item: any): item is LootItem {
  return item && item.type === 'loot';
}

/**
 * Type guard for ConsumableItem
 */
export function isConsumableItem(item: any): item is ConsumableItem {
  return item && item.type === 'consumable' && item.singleUse === true;
}

/**
 * Type guard for ItemEffect
 */
export function isItemEffect(effect: any): effect is ItemEffect {
  return effect && typeof effect.effectType === 'string' && typeof effect.description === 'string';
}

/**
 * Type guard for ItemModifier
 */
export function isItemModifier(modifier: any): modifier is ItemModifier {
  return (
    modifier &&
    typeof modifier.id === 'string' &&
    modifier.source &&
    typeof modifier.target === 'string' &&
    typeof modifier.operation === 'string'
  );
}
